import React, { Children, cloneElement, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, } from 'react';
import { isArrayLike } from 'mobx';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import FormContext from './FormContext';
import { defaultLabelWidth, FIELD_SUFFIX, getProperty, normalizeLabelWidth, getPropertyDSFirst } from './utils';
import { LabelLayout } from './enum';
import Row from '../row';
import Col from '../col';
import { Tooltip as LabelTooltip } from '../core/enum';
import { hide, show } from '../tooltip/singleton';
import isOverflow from '../overflow-tip/util';
const Label = (props) => {
    const { children, className, tooltip, width } = props;
    const { getTooltipTheme, getTooltipPlacement } = useContext(ConfigContext);
    const tooltipRef = useRef(false);
    const style = useMemo(() => width ? ({ width }) : undefined, [width]);
    const handleMouseEnter = useCallback((e) => {
        const { currentTarget } = e;
        if (tooltip === LabelTooltip.always || (tooltip === LabelTooltip.overflow && isOverflow(currentTarget))) {
            show(currentTarget, {
                title: children,
                theme: getTooltipTheme('label'),
                placement: getTooltipPlacement('label'),
            });
            tooltipRef.current = true;
        }
        else if (isArrayLike(tooltip)) {
            const tooltipType = tooltip[0];
            const labelTooltipProps = tooltip[1] || {};
            const duration = (labelTooltipProps.mouseEnterDelay || 0.1) * 1000;
            if (tooltipType === LabelTooltip.always || (tooltipType === LabelTooltip.overflow && isOverflow(currentTarget))) {
                show(currentTarget, {
                    theme: getTooltipTheme('label'),
                    placement: getTooltipPlacement('label'),
                    title: labelTooltipProps.title ? labelTooltipProps.title : children,
                    ...labelTooltipProps,
                }, duration);
                tooltipRef.current = true;
            }
        }
    }, [children, tooltip, tooltipRef]);
    const handleMouseLeave = useCallback(() => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, [tooltipRef]);
    useEffect(() => () => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, [tooltipRef]);
    return (React.createElement("label", { className: className, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, style: style }, children));
};
Label.displayName = 'Label';
const Item = observer((props) => {
    const { getConfig, dataSet, record, labelLayout = getConfig('labelLayout'), labelAlign, labelWidth: contextLabelWidth = defaultLabelWidth, labelTooltip, useColon, getProPrefixCls } = useContext(FormContext);
    const { children, useColon: fieldUseColon = useColon, ...rest } = props;
    const child = Children.only(children);
    if (isValidElement(child)) {
        const prefixCls = getProPrefixCls(FIELD_SUFFIX);
        const { props: childrenProps } = child;
        const { className, fieldClassName, ...otherProps } = childrenProps;
        const fieldProps = { ...rest, ...otherProps };
        const fieldElementProps = {
            className: classNames(props.className, className, prefixCls),
            ...fieldProps,
        };
        const intlFieldOutput = child.type.displayName === 'IntlField' && fieldProps.displayOutput;
        if ([LabelLayout.none, LabelLayout.float, LabelLayout.placeholder].includes(labelLayout)) {
            return (React.createElement("div", { className: `${prefixCls}-wrapper` }, cloneElement(child, fieldElementProps)));
        }
        const label = getProperty(fieldProps, 'label', dataSet, record);
        const required = getPropertyDSFirst(fieldProps, 'required', dataSet, record);
        const readOnly = getProperty(fieldProps, 'readOnly', dataSet, record);
        const isOutput = labelLayout === LabelLayout.horizontal && (child.type.displayName === 'Output' || intlFieldOutput);
        const labelClassName = classNames(`${prefixCls}-label`, `${prefixCls}-label-${labelAlign}`, fieldClassName, {
            [`${prefixCls}-required`]: required && !(child.type.displayName === 'Output' || intlFieldOutput),
            [`${prefixCls}-readonly`]: readOnly,
            [`${prefixCls}-label-vertical`]: labelLayout === LabelLayout.vertical,
            [`${prefixCls}-label-output`]: isOutput,
            [`${prefixCls}-label-useColon`]: label && fieldUseColon,
        });
        const wrapperClassName = classNames(`${prefixCls}-wrapper`, {
            [`${prefixCls}-output`]: isOutput,
        });
        const tooltip = props.labelTooltip || labelTooltip;
        if (labelLayout === LabelLayout.vertical) {
            return (React.createElement(React.Fragment, null,
                React.createElement(Label, { className: labelClassName, tooltip: tooltip }, label),
                React.createElement("div", { className: wrapperClassName }, cloneElement(child, fieldElementProps))));
        }
        const fieldLabelWidth = getProperty(fieldProps, 'labelWidth', dataSet, record);
        const columnLabelWidth = normalizeLabelWidth(contextLabelWidth, 1)[0];
        const labelWidth = columnLabelWidth === 'auto' ? undefined : Math.max(columnLabelWidth, isNaN(fieldLabelWidth) ? 0 : fieldLabelWidth);
        return (React.createElement(Row, { className: `${prefixCls}-row` },
            React.createElement(Col, { className: `${prefixCls}-col` },
                React.createElement(Label, { className: labelClassName, width: labelWidth, tooltip: tooltip }, label)),
            React.createElement(Col, { className: `${prefixCls}-col ${prefixCls}-col-control` },
                React.createElement("div", { className: wrapperClassName }, cloneElement(child, fieldElementProps)))));
    }
    if (child) {
        return child;
    }
    return null;
});
Item.displayName = 'FormItem';
Item.__PRO_FORM_ITEM = true;
export default Item;
